import { useTranslation } from 'react-i18next';
import { CampaignCpmRateList } from './CampaignCpmRateList';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Crown, Flame, Lock } from 'lucide-react';
import { SiInstagram, SiTiktok, SiYoutube } from 'react-icons/si';
import CampaignBudgetBar from '@/components/CampaignBudgetBar';
import { RankingCampaignSummary } from './RankingCampaignSummary';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CampaignBanner } from '@/components/campaigns/CampaignBanner';
import { CampaignCategoryBadges } from '@/components/campaigns/CampaignCategoryBadges';
import { CampaignBannerInfo } from '@/components/campaigns/CampaignBannerInfo';
import type { CommunityCampaignCard } from '@/lib/campaigns/communityCampaignCards';
import { cn } from '@/lib/utils';
import { formatCents } from '@/lib/format';
import { useDragScroll } from '@/hooks/use-drag-scroll';

type Props = {
  cards: CommunityCampaignCard[];
  selectedCampaignId: string | null;
  onToggleSelect: (campaignId: string) => void;
  onOpen: (card: CommunityCampaignCard) => void;
};

function formatRate(card: CommunityCampaignCard) {
  return new Intl.NumberFormat(card.currency === 'USD' ? 'en-US' : 'pt-BR', {
    style: 'currency',
    currency: card.currency,
  }).format(card.cpmRateCents / 100);
}

export function CommunityCampaignCarousel({ cards, selectedCampaignId, onToggleSelect, onOpen }: Props) {
  const { t } = useTranslation();
  // Drag-to-scroll like the channel strip; guardClick keeps the click that
  // ends a drag from also selecting or opening the card it lands on.
  const { ref: scrollRef, dragHandlers, guardClick } = useDragScroll<HTMLDivElement>();
  if (cards.length === 0) return null;

  const scrollBy = (direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: direction === 'left' ? -240 : 240, behavior: 'smooth' });
  };

  return (
    <section className="space-y-2" data-testid="community-campaign-row">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-1.5 text-sm font-bold">
            <Flame className="h-4 w-4 text-primary" />
            {t('communityLibrary.campaignRow.title')}
          </h2>
          <p className="text-xs text-muted-foreground">{t('communityLibrary.campaignRow.subtitle')}</p>
        </div>
        <RouterLink
          to="/earn"
          className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-bold text-primary hover:underline"
        >
          {t('communityLibrary.campaignRow.seeAll')}
          <ChevronRight className="h-3.5 w-3.5" />
        </RouterLink>
      </div>

      <div className="group/campaigns relative">
        <div
          data-testid="community-campaign-fade-left"
          className="pointer-events-none absolute bottom-0 left-0 top-0 z-[15] hidden w-12 bg-gradient-to-r from-background to-transparent sm:block"
        />
        <div
          data-testid="community-campaign-fade-right"
          className="pointer-events-none absolute bottom-0 right-0 top-0 z-[15] hidden w-12 bg-gradient-to-l from-background to-transparent sm:block"
        />
        <button
          type="button"
          onClick={() => scrollBy('left')}
          aria-label={t('communityLibrary.campaignRow.scrollLeft')}
          className="absolute left-1 top-1/2 z-20 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 opacity-0 shadow-sm transition-opacity hover:bg-muted group-hover/campaigns:opacity-100 sm:flex"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div
          ref={scrollRef}
          data-testid="community-campaign-scroller"
          className="flex gap-3 overflow-x-auto scrollbar-hide p-1"
          {...dragHandlers}
          style={{ ...dragHandlers.style, scrollbarWidth: 'none' }}
        >
          {cards.map((card) => {
            const isSelected = selectedCampaignId === card.id;
            const isSelectedLocked = card.isLocked && card.isSelectedMode;
            const article = (
              <article
                key={card.id}
                data-testid={`community-campaign-card-${card.id}`}
                className={cn(
                  // Cards share the row (flex-1) between a compact minimum and
                  // maximum, so every active campaign fits in one view on
                  // desktop and the row only scrolls when there are more.
                  'group/card relative flex min-w-[10.5rem] max-w-[15rem] flex-1 flex-col overflow-hidden rounded-xl border-2 bg-gradient-to-b from-emerald-500/10 via-card to-card',
                  // The overlay button below is the card's main action but sits
                  // under the content and inside overflow-hidden, so its own
                  // focus ring would be invisible: show it on the card instead.
                  'has-[>button:focus-visible]:ring-2 has-[>button:focus-visible]:ring-primary',
                  isSelected
                    ? 'border-emerald-500 ring-2 ring-emerald-500/40 shadow-[0_0_16px_rgba(16,185,129,0.25)]'
                    : card.isLocked
                      ? 'border-border/50'
                      : 'border-emerald-500/40 hover:border-emerald-500/70',
                )}
              >
                {/* Full-card action as a sibling overlay, so the CTA below
                    stays a separate, valid button (no nested interactives). */}
                <button
                  type="button"
                  className="absolute inset-0 z-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-pressed={isSelected}
                  title={card.name}
                  aria-label={t('communityLibrary.campaignRow.filterBy', { name: card.name })}
                  onClick={() => guardClick(() => onToggleSelect(card.id))}
                />
                {/* Categories and platforms ride on the banner, as on /earn; the
                    in-body copies below hide while a banner is shown (:has). */}
                <CampaignBanner
                  src={card.bannerUrl}
                  grayscale={card.isLocked}
                  className="pointer-events-none relative z-10 aspect-[2/1]"
                >
                  <CampaignBannerInfo
                    categories={card.categories}
                    platforms={card.activePlatforms}
                    platformsTestId={`community-campaign-banner-platforms-${card.id}`}
                  />
                </CampaignBanner>
                <div className="pointer-events-none relative z-10 flex flex-1 flex-col gap-1.5 p-2.5">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-shrink-0">
                      {card.avatarSrc ? (
                        <img
                          src={card.avatarSrc}
                          alt=""
                          draggable={false}
                          className={cn('h-8 w-8 rounded-full border-2 object-cover', card.isLocked ? 'border-border/50 grayscale' : 'border-emerald-500/50')}
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-emerald-500/50 bg-emerald-500/20 text-[10px] font-bold text-emerald-300">
                          {card.initials}
                        </div>
                      )}
                      <div className={cn('absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-background', card.isLocked ? 'bg-muted' : 'bg-primary')}>
                        {card.isLocked ? (
                          <Lock className="h-2.5 w-2.5 text-muted-foreground" />
                        ) : card.isProExclusive ? (
                          <Crown className="h-2.5 w-2.5 text-primary-foreground" />
                        ) : (
                          <Flame className="h-2.5 w-2.5 text-primary-foreground" />
                        )}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold">{card.name}</p>
                      {card.campaignType !== 'ranking' && <p className={cn('text-[11px] font-bold leading-tight', card.isLocked ? 'text-muted-foreground' : 'text-emerald-600 dark:text-emerald-400')}>
                        {t('communityLibrary.ratePerThousand', { amount: formatRate(card) })}
                        {card.activePlatforms.length > 0 && (
                          <span
                            data-testid={`community-campaign-body-platforms-${card.id}`}
                            className="ml-1 inline-flex items-center gap-0.5 align-middle text-muted-foreground group-has-[[data-campaign-banner]]/card:hidden"
                          >
                            {card.activePlatforms.includes('tiktok') && (
                              <SiTiktok data-testid="community-campaign-platform-tiktok" className="h-3 w-3" aria-label="TikTok" />
                            )}
                            {card.activePlatforms.includes('instagram') && (
                              <SiInstagram data-testid="community-campaign-platform-instagram" className="h-3 w-3" aria-label="Instagram" />
                            )}
                            {card.activePlatforms.includes('youtube') && (
                              <SiYoutube data-testid="community-campaign-platform-youtube" className="h-3 w-3" aria-label="YouTube" />
                            )}
                          </span>
                        )}
                      </p>}
                      {card.campaignType !== 'ranking' && card.cpmRates.kind === 'per_platform' && (
                        <CampaignCpmRateList
                          rates={card.cpmRates.rates}
                          currency={card.currency}
                          className="mt-0.5 text-[10px]"
                        />
                      )}
                    </div>
                  </div>

                  {card.campaignType === 'ranking' ? <RankingCampaignSummary config={card.rankingConfig} currency={card.currency} /> : <span
                    data-testid={`campaign-chip-min-views-${card.id}`}
                    className={cn(
                      'text-[10px] leading-tight',
                      card.isLocked
                        ? 'text-muted-foreground'
                        : card.minViews.kind === 'minimum'
                          ? 'text-amber-500 dark:text-amber-400'
                          : 'text-emerald-600 dark:text-emerald-400',
                    )}
                  >
                    {card.minViews.kind === 'minimum'
                      ? t('communityLibrary.minViewsShort', { views: card.minViews.views.toLocaleString() })
                      : t('communityLibrary.noMinViews')}
                  </span>}

                  <div
                    data-testid={`community-campaign-body-categories-${card.id}`}
                    className="empty:hidden group-has-[[data-campaign-banner]]/card:hidden"
                  >
                    <CampaignCategoryBadges categories={card.categories} muted={card.isLocked} compact />
                  </div>

                  {card.campaignType !== 'ranking' && card.budgetTotalCents > 0 && (
                    <div className="space-y-1">
                      <div
                        data-testid={`community-campaign-used-${card.id}`}
                        className="flex items-center justify-between gap-2 text-[10px] leading-none"
                      >
                        <span className="text-muted-foreground">{t('communityLibrary.campaignRow.used')}</span>
                        <span className="font-semibold tabular-nums text-foreground">
                          {formatCents(card.budgetSpentCents, card.currency)} · {card.budgetUsedPercent}%
                        </span>
                      </div>
                      {/* Interactive so its pending-budget tooltip still works
                          (the rest of the content lets clicks reach the overlay). */}
                      <div data-testid={`community-campaign-budget-${card.id}`} className="pointer-events-auto">
                        <CampaignBudgetBar
                          totalCents={card.budgetTotalCents}
                          spentCents={card.budgetSpentCents}
                          pendingCents={card.budgetPendingCents}
                          currency={card.currency}
                          size="sm"
                        />
                      </div>
                    </div>
                  )}

                  <div className="mt-auto pt-0.5">
                    {isSelectedLocked ? (
                      // Not a button on purpose: a paid plan cannot open a
                      // selected-only campaign, so there is no upsell to offer.
                      <span className="inline-flex w-full items-center justify-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] font-bold text-muted-foreground">
                        <Lock className="h-3 w-3 flex-shrink-0" />
                        {t('communityLibrary.selectedOnlyCta')}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          guardClick(() => onOpen(card));
                        }}
                        className="pointer-events-auto inline-flex w-full items-center justify-center gap-1 rounded-md bg-primary px-2 py-1 text-[11px] font-bold text-primary-foreground transition-colors hover:bg-primary/90"
                      >
                        {card.isLocked ? (
                          <>
                            <Crown className="h-3 w-3" />
                            {t('communityLibrary.subscribeProCta')}
                          </>
                        ) : (
                          <>
                            {t('communityLibrary.seeCampaign')}
                            <ChevronRight className="h-3 w-3" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );

            if (!card.isLocked) return article;
            return (
              <Tooltip key={card.id}>
                <TooltipTrigger asChild>{article}</TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px] text-center text-xs">
                  {card.isSelectedMode ? t('earnPage.selectedOnly.popoverText') : t('earnPage.inviteOnly.popoverText')}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => scrollBy('right')}
          aria-label={t('communityLibrary.campaignRow.scrollRight')}
          className="absolute right-1 top-1/2 z-20 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 opacity-0 shadow-sm transition-opacity hover:bg-muted group-hover/campaigns:opacity-100 sm:flex"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}
